import cloneDeep from 'lodash/cloneDeep';

import {
  defaultGroupName,
  defaultResponseCount,
  instantSurveyGroupRefUrlVariable,
  placeholderImage,
  questionLogicOperators,
  surveyDatePromptIds,
  surveyDescriptionDefault,
  surveyDescriptionLong,
  surveyDescriptionShort,
  surveyLengthLong,
  surveyLengthShort,
  surveyTypeQuestionId,
} from './constants';

import { mergeAndFilterAddons } from '../../../helpers/instant_survey';

import { transformPrompt } from '@src/app/pages/Dashboard/components/Builder/utilities/helpers.js';

/**
 * @typedef {Object} Addon
 * @property {Number} addonType
 * @property {String} [description]
 * @property {Number} id
 * @property {Object.<string, number>} prices
 * @property {Boolean} [selected]
 * @property {String} title
 */

/**
 * @typedef {Object} Question
 * @property {Object[]} [answers]
 * @property {String|Null} caption
 * @property {Number} id
 * @property {Object} image
 * @property {String} [image.imageBucketName]
 * @property {String} image.imageCdnUrl
 * @property {String} [image.imageObjectKey]
 * @property {String} [image.imageObjectType]
 * @property {Boolean} [isFolder]
 * @property {Number|Null} [parentId]
 * @property {String} text
 * @property {String} type
 */

/**
 * Transforms the create survey API response
 *
 * @param {Object} response
 * @return {Object}
 */
export const transformCreateSurvey = (response = {}) => {
  const { instant_survey, stripe_checkout_url, subscriptions, user_credits_remaining } = response;

  const hasSurveyRecord =
    Object.prototype.hasOwnProperty.call(response, 'id') &&
    (typeof response.title === 'string' ||
      Object.prototype.hasOwnProperty.call(response, 'payment_type') ||
      Object.prototype.hasOwnProperty.call(response, 'atlas_instant_survey_ref'));

  const surveyData = instant_survey ?? (hasSurveyRecord ? response : undefined);

  return {
    instantSurvey: surveyData ? transformSurvey(surveyData) : undefined,
    stripeCheckoutUrl: stripe_checkout_url,
    subscriptions: subscriptions?.map((sub) => ({
      subscriptionId: sub.subscription_id,
      totalRemainingCompletes: sub.remaining_completes,
      totalUsedCompletes: sub.used_completes,
    })),
    userCreditsRemaining: user_credits_remaining,
  };
};

/**
 * @typedef {Object} Answer
 * @property {Number} id
 * @property {String} imageCdnUrl
 * @property {Boolean} isAnchored
 * @property {Boolean} isExclusive
 * @property {Boolean} isNoneOfTheAbove
 * @property {Boolean} isOther
 * @property {String} text
 */

/**
 * transformQuestionAnswer
 * @param {Boolean} answer.anchored
 * @param {Boolean} answer.exclusive
 * @param {Number} answer.id
 * @param {String} answer.image_cdn_url
 * @param {Boolean} answer.none_of_the_above
 * @param {Boolean} answer.other
 * @param {String} answer.text
 * @return {Answer}
 */
export const transformQuestionAnswer = ({
  anchored: isAnchored,
  exclusive: isExclusive,
  id,
  image_cdn_url: imageCdnUrl,
  none_of_the_above: isNoneOfTheAbove,
  other: isOther,
  text,
}) => ({
  id,
  ...(imageCdnUrl && {
    imageCdnUrl: imageCdnUrl.indexOf('http://localhost') !== -1 ? placeholderImage : imageCdnUrl,
  }),
  isAnchored,
  isExclusive,
  isNoneOfTheAbove,
  isOther,
  text,
});

/**
 * transformQuestionLogic
 * @param {Object} options.questionLogic
 * @param {Question[]} options.questions
 * @return {Object}
 */
export const transformQuestionLogic = ({ questionLogic, questions }) => {
  let hasQuestionLogicError = false;
  const modifiedQuestionLogic = cloneDeep(questionLogic);
  // Accept legacy disqualify logic format
  for (const questionId in modifiedQuestionLogic) {
    const disqualify = modifiedQuestionLogic[questionId]?.disqualify;
    if (disqualify && !Array.isArray(disqualify.logic) && Array.isArray(disqualify.answers)) {
      const legacyOperator = disqualify.operator || 'ANY';
      disqualify.logic = [
        {
          answers: disqualify.answers,
          operator: legacyOperator,
        },
      ];
      disqualify.operator = 'ANY';
      delete disqualify.answers;
    }
  }

  for (const questionId in modifiedQuestionLogic) {
    const question = modifiedQuestionLogic[questionId];
    if (question.disqualify) {
      question.disqualify.operator = questionLogicOperators[question.disqualify.operator];
      const disqualifyQuestion = questions.find((question) => question.id === parseInt(questionId));
      question.disqualify.logic = (question.disqualify.logic || []).map((row) => {
        if (row.url_variable === instantSurveyGroupRefUrlVariable) {
          if ((row.values || []).length === 0) {
            hasQuestionLogicError = true;
          }
          return {
            hasAnswerError: false,
            operator: questionLogicOperators[row.operator],
            url_variable: instantSurveyGroupRefUrlVariable,
            values: row.values,
          };
        }
        const initialAnswersLength = row.answers.length;
        const answers = row.answers
          .map((answerId) => {
            return disqualifyQuestion.answers.find((answer) => answer.id === answerId);
          })
          .filter((answer) => !!answer);
        if (answers.length === 0 || answers.length !== initialAnswersLength) {
          hasQuestionLogicError = true;
        }
        return {
          answers,
          hasAnswerError: false,
          isFirstAnswerError: false,
          operator: questionLogicOperators[row.operator],
        };
      });
    }
    if (question.skip) {
      question.skip.operator = questionLogicOperators[question.skip.operator];
      // eslint-disable-next-line no-loop-func
      question.skip.logic = question.skip.logic.map((row) => {
        if (row.url_variable === instantSurveyGroupRefUrlVariable) {
          if (row.values.length === 0) {
            hasQuestionLogicError = true;
          }
          return {
            operator: questionLogicOperators[row.operator],
            url_variable: instantSurveyGroupRefUrlVariable,
            values: row.values,
          };
        } else if (row.comparison_value) {
          const skipQuestion = questions.find((question) => question.id === row.question);
          if (!row.comparison_value) {
            hasQuestionLogicError = true;
          }

          return {
            comparison_value: row.comparison_value,
            operator: questionLogicOperators[row.operator],
            question: skipQuestion,
          };
        } else {
          const skipQuestion = questions.find((question) => question.id === row.question);
          const answers = row.answers
            .map((answerId) => {
              return skipQuestion.answers.find((answer) => answer.id === answerId);
            })
            .filter((answer) => !!answer);
          if (answers.length === 0 || answers.length !== row.answers.length) {
            hasQuestionLogicError = true;
          }

          return {
            answers,
            operator: questionLogicOperators[row.operator],
            question: skipQuestion,
          };
        }
      });
    }
  }
  return { hasQuestionLogicError, questionLogic: modifiedQuestionLogic };
};

/**
 * transformQuestion
 * @param {Object} question
 * @param {String} question.caption
 * @param {Number} question.id
 * @param {String} question.image_bucket_name
 * @param {String} question.image_cdn_url
 * @param {String} question.image_object_key
 * @param {String} question.image_object_type
 * @param {String[]} question.options
 * @param {Number} question.parent_id
 * @param {String} question.question_type
 * @param {Boolean} question.randomize_options
 * @param {String} question.text
 * @return {Question}
 */
export const transformQuestion = ({
  caption,
  id,
  image_bucket_name: imageBucketName,
  image_cdn_url: imageCdnUrl,
  image_object_key: imageObjectKey,
  image_object_type: imageObjectType,
  options: answers,
  parent_id: parentId,
  question_type: type,
  randomize_options: randomizeOptions,
  text,
}) => ({
  answers: answers.map(transformQuestionAnswer),
  caption,
  id,
  image: {
    imageBucketName,
    imageCdnUrl:
      imageCdnUrl && imageCdnUrl.indexOf('http://localhost') !== -1
        ? placeholderImage
        : imageCdnUrl,
    imageObjectKey,
    imageObjectType,
  },
  isFolder: false,
  parentId,
  randomizeOptions,
  text,
  type,
});

/**
 * @typedef {Object} Survey
 * @property {Addon[]} addons
 * @property {Object} answers
 * @property {?String} atlasInstantSurveyRef
 * @property {Number} completed
 * @property {Date} created_datetime
 * @property {String} creator
 * @property {Object[]} groups
 * @property {Number} id
 * @property {String} instantSurveyTypeId
 * @property {String} instantSurveyTypeName
 * @property {Boolean} isLocked
 * @property {Boolean} isOwner
 * @property {Boolean} isPaused
 * @property {Number} issued
 * @property {Object} questionLogic
 * @property {Array} questions
 * @property {Number} status
 * @property {String} title
 * @property {?Number} validationKey
 */

/**
 * transformSurvey
 * @param {Object} survey
 * @param {Object[]} survey.addons
 * @param {Object} survey.answers
 * @param {?String} survey.atlas_contact_list_ref
 * @param {?String} survey.atlas_instant_survey_ref
 * @param {?String} survey.atlas_job_ref
 * @param {?String} survey.atlas_survey_ref
 * @param {?String} survey.ask_why_document_id
 * @param {?String} survey.ask_why_job_id
 * @param {?String} survey.ask_why_people_group_type_id
 * @param {Number} survey.base_size
 * @param {Number} survey.completed
 * @param {?Date} survey.complete_datetime
 * @param {Date} survey.created_datetime
 * @param {String} survey.id
 * @param {String} survey.instant_survey_type_id
 * @param {String} survey.instant_survey_type_name
 * @param {Number} survey.isc_survey_id
 * @param {Number} survey.issued
 * @param {Boolean} survey.is_owner
 * @param {?Number} survey.number_of_responses_requested
 * @param {Boolean} survey.paused
 * @param {?String} survey.payment_amount
 * @param {?String} survey.payment_status
 * @param {?String} survey.payment_type
 * @param {String} survey.provider_report_public_url
 * @param {String} survey.provider_report_ref
 * @param {Object} survey.question_logic
 * @param {Number} survey.status
 * @param {?String} survey.survey_source_id
 * @param {String} survey.title
 * @param {Object} survey.user
 * @param {?Number} survey.validation_pk
 * @return {Survey}
 */
export const transformSurvey = ({
  addons,
  answers = {},
  atlas_instant_survey_ref: atlasInstantSurveyRef,
  completed,
  created_datetime,
  id,
  instant_survey_type_id: instantSurveyTypeId,
  instant_survey_type_name: instantSurveyTypeName,
  is_owner: isOwner,
  issued,
  number_of_responses_requested: numberOfResponsesRequested,
  paused: isPaused,
  payment_type: paymentType,
  question_logic: questionLogic,
  service_token: serviceToken,
  status,
  survey_source_id: surveySourceId,
  title,
  user,
  validation_pk: validationKey,
}) => {
  const questions = (answers[surveyTypeQuestionId[instantSurveyTypeId]] || []).map(
    ({
      caption,
      id,
      image_cdn_url: imageCdnUrl,
      name: text,
      options: answers = [], // INSRV-1607: some v1 survey questions do not have options when retrieved
      parent_id: parentId,
      question_type: type,
      randomize_options: randomizeOptions,
    }) => ({
      answers: answers.map(transformQuestionAnswer),
      caption,
      id: Number(id.split(':')[1]),
      image: {
        imageCdnUrl:
          imageCdnUrl && imageCdnUrl.indexOf('http://localhost') !== -1
            ? placeholderImage
            : imageCdnUrl,
      },
      isFolder: false,
      parentId,
      randomizeOptions,
      text,
      type,
    })
  );
  delete answers[surveyTypeQuestionId[instantSurveyTypeId]];

  return {
    addons: addons?.map(transformSurveyAddon) || [],
    atlasInstantSurveyRef,
    completed,
    created_datetime, // this is not camelCased due to the ListView component
    creator: `${user?.last_name}, ${user?.first_name}`,
    groups: [
      {
        answers,
        completed,
        id: `default-group-${id}`,
        issued,
        name: defaultGroupName,
        responseCount: numberOfResponsesRequested || defaultResponseCount,
      },
    ],
    id,
    instantSurveyTypeId,
    instantSurveyTypeName,
    isLocked: !isOwner,
    isOwner,
    isPaused,
    issued,
    paymentType: paymentType.toLowerCase(),
    questions,
    serviceToken,
    status,
    surveySourceId,
    title,
    validationKey,
    ...transformQuestionLogic({ questionLogic, questions }),
  };
};

/**
 * transformSurveyAddon
 * @param {Object} surveyAddon
 * @param {Number} surveyAddon.addon_type
 * @param {Number} surveyAddon.id
 * @param {String} surveyAddon.payment_amount
 * @param {String} surveyAddon.payment_type
 * @param {String} surveyAddon.title
 * @return {Addon}
 */
export const transformSurveyAddon = ({ addon_type, id, payment_amount, payment_type, title }) => {
  const amount = parseFloat(payment_amount) || 0;
  const paymentType = payment_type?.toLowerCase();

  return {
    addonType: addon_type,
    id,
    prices: {
      completes: paymentType === 'completes' ? amount : 0,
      credits: paymentType === 'credits' ? amount : 0,
    },
    title,
  };
};

/**
 * @typedef {Object} SurveyListItem
 * @property {String} [atlasInstantSurveyRef]
 * @property {String} [atlasSurveyRef]
 * @property {String} created_datetime snake_case for ListPage
 * @property {String} creator
 * @property {Number} id
 * @property {String} instantSurveyTypeId
 * @property {String} instantSurveyTypeName
 * @property {Boolean} isSurveyDeprecated
 * @property {Boolean} isOwner
 * @property {Boolean} isPaused
 * @property {Number} status
 * @property {String} title
 */

/**
 * transformSurveyList
 * @param {Object} response v2 list endpoint paginated object
 * @param {Object[]} response.items
 * @param {Number} response.page
 * @param {Number} response.pages
 * @param {Number} response.size
 * @param {Number} response.total
 * @return {Object}
 */
export const transformSurveyList = ({ items, page, pages, size, total }) => {
  return {
    items: items.map(transformSurveyListItem),
    page,
    pages,
    size,
    total,
  };
};

/**
 * transformSurveyListItem
 * @param {Object} survey
 * @param {String} [survey.atlas_instant_survey_ref]
 * @param {String} [survey.atlas_survey_ref]
 * @param {Number} survey.base_size
 * @param {Number} survey.completed
 * @param {Date} survey.created_datetime
 * @param {String} survey.creator
 * @param {Number} survey.id
 * @param {String} survey.instant_survey_type_id
 * @param {String} survey.instant_survey_type_name
 * @param {Boolean} survey.is_owner
 * @param {Boolean} survey.is_survey_type_deprecated
 * @param {Number} survey.issued
 * @param {Boolean} survey.paused
 * @param {Number} survey.status
 * @param {String} survey.title
 * @returns {SurveyListItem}
 */
const transformSurveyListItem = ({
  atlas_instant_survey_ref: atlasInstantSurveyRef,
  atlas_survey_ref: atlasSurveyRef,
  created_datetime,
  creator,
  id,
  instant_survey_type_id: instantSurveyTypeId,
  instant_survey_type_name: instantSurveyTypeName,
  is_owner: isOwner,
  is_survey_type_deprecated: isSurveyDeprecated,
  paused: isPaused,
  status,
  title,
}) => {
  return {
    atlasInstantSurveyRef,
    atlasSurveyRef,
    created_datetime,
    creator,
    id,
    instantSurveyTypeId,
    instantSurveyTypeName,
    isOwner,
    isPaused,
    isSurveyDeprecated,
    status,
    title,
  };
};

/**
 * @typedef {Object} SurveyType
 * @property {Object[]} advancedPrompts
 * @property {Object} default_answers
 * @property {String} description
 * @property {Boolean} enabled
 * @property {String} help_url
 * @property {String[][]} hierarchy_search_prompts
 * @property {Addon[]} instantSurveyAddons
 * @property {String} id
 * @property {Number} maxQuestions
 * @property {String} name
 * @property {Object[]} prompts
 * @property {String} prompts_madlib
 * @property {Object[]} questionnairePricing
 * @property {Object[]} samplePricing
 */

/**
 * transformSurveyType
 * @param {Object} surveyType
 * @param {String} surveyType.description
 * @param {Boolean} surveyType.enabled
 * @param {String} surveyType.help_url
 * @param {String[][]} surveyType.hierarchy_search_prompts eg: [ ['categories', 'product'] ]
 * @param {String} surveyType.id
 * @param {Object[]} [surveyType.instant_survey_addons]
 * @param {Object[]} [surveyType.instant_survey_pricechart_sample]
 * @param {Object[]} [surveyType.instant_survey_pricechart_questionnaire]
 * @param {String} surveyType.name
 * @param {Object[]} surveyType.prompts
 * @param {String} surveyType.prompts_madlib
 * @return {SurveyType}
 */
export const transformSurveyType = ({
  id,
  instant_survey_addons = [],
  instant_survey_pricechart_questionnaire = [],
  instant_survey_pricechart_sample = [],
  prompts,
  prompts_madlib,
  ...data
}) => {
  const dateRangeIndex = prompts.findIndex((prompt) => surveyDatePromptIds.includes(prompt.id));
  if (dateRangeIndex >= 0) {
    prompts[dateRangeIndex].max = 1; // date range max is missing for instant survey types
  }

  const instantSurveyAddons =
    mergeAndFilterAddons(instant_survey_addons).map(transformSurveyTypeAddon);
  const maxQuestions = Math.max(
    ...instant_survey_pricechart_questionnaire.map(({ number_of_questions }) => number_of_questions)
  );
  const questionnairePricing = instant_survey_pricechart_questionnaire.map(
    ({ credits_cost_questionnaire: amount, id, number_of_questions: questions }) => ({
      amount: Number(amount),
      description:
        questions <= surveyLengthShort
          ? surveyDescriptionShort
          : questions >= surveyLengthLong
            ? surveyDescriptionLong
            : surveyDescriptionDefault,
      id,
      questions,
    })
  );
  const samplePricing = instant_survey_pricechart_sample.map(
    ({
      ask_why: isAskWhy,
      id,
      max_responses: responses,
      number_of_quota_groups: quotaGroups,
      payment_amount: amount,
      payment_type: type,
    }) => ({
      amount: Number(amount),
      id,
      isAskWhy,
      quotaGroups,
      responses,
      type,
    })
  );

  const questionsIndex = prompts.findIndex((prompt) => prompt.id === surveyTypeQuestionId[id]);
  // remove questions from survey prompts
  if (questionsIndex >= 0) {
    let madlibIndex = 0;
    return {
      ...data,
      advancedPrompts: prompts
        .filter(({ advanced }, index) => advanced && index !== questionsIndex)
        .map(transformPrompt),
      default_answers: {}, // fix for date ranges being all pre-selected
      id,
      instantSurveyAddons: [],
      maxQuestions,
      prompts: prompts
        .filter(({ advanced }, index) => !advanced && index !== questionsIndex)
        .map(transformPrompt),
      prompts_madlib: prompts_madlib.replace(/\{x\}/g, (match) => {
        const returnString = madlibIndex === questionsIndex ? 'questions' : match;
        madlibIndex++;
        return returnString;
      }),
      questionnairePricing,
      samplePricing,
    };
  }

  return {
    ...data,
    advancedPrompts: prompts.filter(({ advanced }) => advanced).map(transformPrompt),
    default_answers: {},
    id,
    instantSurveyAddons,
    maxQuestions,
    prompts: prompts.filter(({ advanced }) => !advanced).map(transformPrompt),
    prompts_madlib,
    questionnairePricing,
    samplePricing,
  };
};

/**
 * transformSurveyTypeAddon
 * @param {Object} surveyTypeAddon
 * @param {Number} surveyTypeAddon.completes
 * @param {Number} surveyTypeAddon.credits
 * @param {String} surveyTypeAddon.description
 * @param {Boolean} [surveyTypeAddon.selected]
 * @param {String} surveyTypeAddon.title
 * @param {Number} surveyTypeAddon.value
 * @return {Addon}
 */
export const transformSurveyTypeAddon = ({
  completes,
  credits,
  description,
  selected = false,
  title,
  value,
}) => {
  return {
    addonType: value,
    description,
    id: value,
    prices: {
      completes,
      credits,
    },
    selected,
    title,
  };
};
